import React, { useEffect, useMemo, useState } from "react";
import { Input, Select, Button, Radio, message, Form } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import SingleSelectButtonGroup from "../Partner/SingleSelectButtonGroup";
import DeleteIcon from "./DeleteIcon";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  SaveBrandDigitalInfo,
  SaveBrandInfo,
} from "../../API/endpoints/Brands";
import { GetAllOrg } from "../../API/endpoints/Organization";
import FacebookIcon from "../../components/icons/FacebookIcon";
import LinkedinIcon from "../../components/icons/LinkedinIcon";
import TwitterIcon from "../../components/icons/TwitterIcon";
import { marketplacesOptions } from "../../constants/dataConstants";
import InstagramIcon from "../components/BrandsPartnerComponents/InstagramIcon";

interface Props {
  setDefaultTab: (tab: string) => void;
  setBrandId: (brandId: string) => void;
  setBrandName: (brandName: string) => void;
  brandName: string;
  selectYearsOperations: string;
  setSelectYearsOperations: (years: string) => void;
  currentRevenueStatus: string;
  setCurrentRevenueStatus: (revenue: string) => void;
  socialLinks: any;
  setSocialLinks: any;
  marketplaces: any;
  setMarketplaces: any;
  setSelectOrg: any;
}

const { Option } = Select;

const BasicInfo = (props: Props) => {
  const {
    brandName,
    setBrandName,
    selectYearsOperations,
    setSelectYearsOperations,
    currentRevenueStatus,
    setCurrentRevenueStatus,
    socialLinks,
    setSocialLinks,
    marketplaces,
    setMarketplaces,
    setSelectOrg,
  } = props;
  const params = useParams();
  const pathname = useLocation();

  const [operationChecked, setOperationChecked] = useState(true);
  const [orgList, setOrgList] = useState([]);

  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [revenueError, setRevenueError] = useState("");

  useEffect(() => {
    if (pathname?.pathname === "/addnewbrand") {
      GetAllOrg()
        .then((res: any) => {
          setOrgList(res?.data?.rows || []);
        })
        .catch((err: any) => {
          console.error("Something went wrong:", err);
        });
    }
  }, []);

  const handleAddSocial = () => {
    setSocialLinks([...socialLinks, { platform: "", url: "" }]);
  };

  const handleAddMarketplace = () => {
    setMarketplaces([...marketplaces, { platform: "", url: "" }]);
  };

  const handleSocialChange = (index: any, key: any, value: any) => {
    const updated: any = [...socialLinks];
    updated[index][key] = value;
    setSocialLinks(updated);
  };

  const handleMarketplaceChange = (index: any, key: any, value: any) => {
    const updated: any = [...marketplaces];
    updated[index][key] = value;
    setMarketplaces(updated);
  };

  const handleRemove = (index: any, type: any) => {
    if (type === "social") {
      const updated = socialLinks.filter((_: any, i: any) => i !== index);
      setSocialLinks(updated);
    } else {
      const updated = marketplaces.filter((_: any, i: any) => i !== index);
      setMarketplaces(updated);
    }
  };

  const onSubmit = async () => {
    if (error || revenueError) {
      message.error(error || revenueError);
      return;
    }
    if (!brandName.trim()) {
      message.error("Please enter the brand name.");
      return;
    }
    if (!selectYearsOperations) {
      message.error("Please select years of operation.");
      return;
    }
    if (!currentRevenueStatus.trim()) {
      message.error("Please enter the current revenue status.");
      return;
    }
    props.setDefaultTab("focus-category");
  };

  const validateBrandName = (value: string) => {
    const hasAlphabet = /[A-Za-z]/.test(value);
    if (!hasAlphabet) {
      setError("Brand name must contain at least one alphabetic character.");
    } else if (value.length > 50) {
      setError("Brand name must not exceed 50 characters.");
    } else {
      setError("");
    }
  };

  const handleChangeRevenue = (e: any) => {
    const value = e.target.value;
    setCurrentRevenueStatus(value.replace(/,/g, '').trim());
    validateRevenue(value);
  };

  const validateRevenue = (value: string) => {
    // Remove commas and trim whitespace
    const sanitizedValue = value.replace(/,/g, '').trim();

    const isValidNumber = !isNaN(Number(sanitizedValue)) && Number(sanitizedValue) > 0;

    if (!sanitizedValue) {
      setRevenueError("Revenue amount is required.");
    } else if (!isValidNumber) {
      setRevenueError("Please enter a valid, positive revenue amount.");
    } else {
      setRevenueError("");
    }
  };

  const handleChangeBrandName = (e: any) => {
    const value = e.target.value;
    setBrandName(value);
    validateBrandName(value);
  };

  const socialLinkOptions = [
    {
      label: "Facebook",
      value: "facebook",
      icon: <FacebookIcon />
    },
    {
      label: "Instagram",
      value: "instagram",
      icon: <InstagramIcon />
    },
    {
      label: "LinkedIn",
      value: "linkedin",
      icon: <LinkedinIcon />
    },
    {
      label: "Twitter",
      value: "twitter",
      icon: <TwitterIcon />
    }
  ]

  const marketplacePlatforms = useMemo(() => {
    return marketplaces?.map((m: any) => m?.platform)
  }, [marketplaces])

  const socialPlatforms = useMemo(() => {
    return socialLinks?.map((m: any) => m?.platform)
  }, [socialLinks])

  return (
    <div className="p-6 ">
      <div className="flex flex-col gap-[24px] ">
        {pathname?.pathname === "/addnewbrand" ? (
          <div>
            <h2 className="text-[14px] text-[#1D2939] font-[500]">
              Select Organization
            </h2>
            <Select
              options={orgList}
              fieldNames={{ label: "company_name", value: "id" }}
              placeholder="Select Organization"
              className="!w-[46%] "
              onChange={(e) => setSelectOrg(e)}
            />
          </div>
        ) : (
          <></>
        )}
        <div>
          <h2 className="text-[14px] text-[#1D2939] font-[500]">Brand name</h2>
          <Form.Item
            validateStatus={error ? "error" : ""}
            help={error || ""}
          >
            <Input
              value={brandName}
              onChange={handleChangeBrandName}
              placeholder="Brand name"
              className="max-w-md"

            />
          </Form.Item>
        </div>
        <div>
          <h2 className="text-[14px] text-[#1D2939] font-[500]">
            Years of Operation
          </h2>
          <SingleSelectButtonGroup
            label=""
            options={["0-1 years", "1-2 years", "2-5 years", "5+ years"]}
            selected={selectYearsOperations}
            setSelected={setSelectYearsOperations}
            checked={operationChecked}
            setChecked={setOperationChecked}
            showCheckBox={false}
            checkboxClassName="!m-[0]"
          />
        </div>

        <div>
          <h2 className="text-[14px] text-[#1D2939] font-[500]">
            Current Revenue Status (USD)
          </h2>
          <Form.Item
            validateStatus={revenueError ? "error" : ""}
            help={revenueError || ""}
          >
            <Input
              value={currentRevenueStatus}
              onChange={(e) => handleChangeRevenue(e)}
              placeholder="$500,000"
              className="max-w-md"
            />
          </Form.Item>
        </div>

        <div>
          <h2 className="mt-6 text-[14px] text-[#1D2939] font-[500]">
            Social Media Presence
          </h2>
          {socialLinks.map((item: any, index: any) => (
            <div className="flex gap-4 mb-3" key={index}>
              <Select
                className="basis-[20%] "
                placeholder="Select platform"
                value={item.platform}
                onChange={(value) =>
                  handleSocialChange(index, "platform", value)
                }
              >
                {
                  socialLinkOptions?.filter((o) => !socialPlatforms.includes(o?.value) || item.platform === o?.value)?.map((o) => (
                    <Option value={o?.value}>
                      <p className="flex m-[0] items-center gap-[8px] ">
                        {o?.icon}{" "}
                        {o?.label}
                      </p>
                    </Option>
                  ))
                }
              </Select>
              <Input
                className="basis-[70%] "
                placeholder="Enter URL"
                value={item.url}
                onChange={(e) =>
                  handleSocialChange(index, "url", e.target.value)
                }
              />
              {index !== 0 && (
                <Button
                  className="basis-[5%] border-none "
                  icon={<DeleteIcon />}
                  onClick={() => handleRemove(index, "social")}
                />
              )}
            </div>
          ))}
          <Button type="link" icon={<PlusOutlined />} onClick={handleAddSocial}>
            Add New
          </Button>
        </div>
        <div>
          <h2 className="text-[14px] text-[#1D2939] font-[500]">
            Sales Marketplaces
          </h2>
          {marketplaces.map((item: any, index: any) => (
            <div className="flex gap-4 mb-3" key={index}>
              <Select
                className="basis-[20%] "
                placeholder="Select marketplace"
                value={item.platform}
                onChange={(value) =>
                  handleMarketplaceChange(index, "platform", value)
                }
                options={marketplacesOptions?.filter((o) => !marketplacePlatforms.includes(o?.value) || item.platform === o?.value)}
              />
              <Input
                className="basis-[70%]"
                placeholder="Enter URL"
                value={item.url}
                onChange={(e) =>
                  handleMarketplaceChange(index, "url", e.target.value)
                }
              />
              {index !== 0 && (
                <Button
                  className="basis-[5%] border-none "
                  icon={<DeleteIcon />}
                  onClick={() => handleRemove(index, "marketplace")}
                />
              )}
            </div>
          ))}
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={handleAddMarketplace}
          >
            Add New
          </Button>
        </div>
      </div>
      <div className="flex justify-end mt-6">
        <Button className="mr-2" onClick={() => navigate(-1)}>Cancel</Button>
        <Button onClick={onSubmit} className="!bg-[#24a0ed]" type="primary">
          Save & Continue
        </Button>
      </div>
    </div>
  );
};

export default BasicInfo;
